"use client";

import { useState, type FormEvent, type ReactNode } from "react";
import { Flag, LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateContentReport } from "@/hooks/use-admin-platform";
import {
  REPORT_REASON_LABELS,
  type ReportReason,
  type ReportTargetType,
} from "@/lib/admin/platform-types";
import { getErrorMessage } from "@/lib/utils";

const REASONS = Object.keys(REPORT_REASON_LABELS) as ReportReason[];

export function ContentReportDialog({
  targetType,
  targetId,
  trigger,
  compact = false,
}: {
  targetType: ReportTargetType;
  targetId: string;
  trigger?: ReactNode;
  compact?: boolean;
}) {
  const reportMutation = useCreateContentReport();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>("spam");
  const [details, setDetails] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await reportMutation.mutateAsync({
      targetType,
      targetId,
      reason,
      details: details.trim() || undefined,
    });
    setSubmitted(true);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSubmitted(false);
          setDetails("");
          setReason("spam");
        }
      }}
    >
      <DialogTrigger asChild>
        {trigger ??
          (compact ? (
            <button
              type="button"
              className="font-medium hover:text-foreground"
            >
              Jelentés
            </button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Tartalom jelentése"
            >
              <Flag className="size-4" />
            </Button>
          ))}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tartalom jelentése</DialogTitle>
          <DialogDescription>
            A jelentést a platform moderátorai látják. A saját bejegyzéseidet
            itt nem kell jelezned.
          </DialogDescription>
        </DialogHeader>
        {submitted ? (
          <p className="text-sm text-muted-foreground">
            Köszönjük, a jelentést rögzítettük.
          </p>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="report-reason">Ok</Label>
              <select
                id="report-reason"
                value={reason}
                onChange={(event) =>
                  setReason(event.target.value as ReportReason)
                }
                className="h-10 w-full rounded-lg border bg-muted/40 px-3 text-sm"
              >
                {REASONS.map((item) => (
                  <option key={item} value={item}>
                    {REPORT_REASON_LABELS[item]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="report-details">Részletek (opcionális)</Label>
              <Textarea
                id="report-details"
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                maxLength={1000}
                placeholder="Rövid indoklás a moderátoroknak"
              />
            </div>
            {reportMutation.isError ? (
              <p className="text-sm text-destructive">
                {getErrorMessage(reportMutation.error)}
              </p>
            ) : null}
            <Button type="submit" disabled={reportMutation.isPending}>
              {reportMutation.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : null}
              Jelentés küldése
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
